"use client";

import { FEATURE_FLAGS, METRICS_EVENTS } from "@cede/types";
import { WheelSpinIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { InfoCard } from "@/components/ui/info-card";
import { SkeletonElement } from "@/components/ui/skeleton";
import { ROUTES } from "@/constants/routes";
import { useTickets, useDevice } from "@/hooks";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { cn } from "@/lib/utils";
import { useSpinningWheelDialogStore } from "@/store/spinningWheelDialogStore";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useRouter } from "next/navigation";

/** Wheel-ticket balance + wheel/quests CTAs. Renders nothing when quests are disabled. */
export const WheelTicketsCard = () => {
  const router = useRouter();
  const { isMobile } = useDevice();
  const { tickets: userTickets, isLoading: isLoadingTickets } = useTickets({ enabled: true });
  const isQuestsEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.QUESTS_HUB_ENABLE);
  const disableLottieAnimations = useFeatureFlagIsOn(FEATURE_FLAGS.PERFORMANCE_HUB_DISABLE_LOTTIE);
  const { handleOpen: handleOpenWheel } = useSpinningWheelDialogStore();
  const { trackEvent } = useMetricsEvents();

  if (!isQuestsEnabled) return null;

  const handleOpenWheelClick = () => {
    trackEvent(METRICS_EVENTS.WHEEL_OPEN_FROM_PROFILE_REWARDS, {});
    handleOpenWheel();
  };

  return (
    <InfoCard
      title="Wheel Tickets"
      padding={isMobile ? "none" : "md"}
      withBackground={!isMobile}
      withBorder={!isMobile}
      gap="lg"
      className={cn("flex min-w-0 flex-col")}
      contentClassName="flex flex-1 flex-col justify-between gap-5"
      titleClassName="text-lg font-semibold text-cede-text-primary"
    >
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center">
          <WheelSpinIcon fill="var(--color-cede-text-accent)" width={38} height={38} />
        </div>
        {isLoadingTickets ? (
          <SkeletonElement className="h-[60px] w-[60px] rounded-lg" />
        ) : (
          <span className="text-5xl font-bold text-cede-text-accent">{userTickets || 0}</span>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <Button
          variant="primary"
          size="medium"
          onClick={handleOpenWheelClick}
          className="flex w-full items-center justify-center gap-2"
        >
          <div className="flex scale-125 items-center justify-center w-6 h-6">
            {disableLottieAnimations ? (
              <WheelSpinIcon width={24} height={24} className="text-cede-bg-primary" />
            ) : (
              <DotLottieReact src="/lottie/wheel.lottie" loop autoplay />
            )}
          </div>
          <span>The Wheel</span>
        </Button>
        <Button variant="secondary" size="medium" onClick={() => router.push(ROUTES.QUESTS)} className="w-full">
          Get More Tickets
        </Button>
      </div>
    </InfoCard>
  );
};
